import { Box, Typography, Fade, useTheme } from "@mui/material";

const LoadingSite = ({ title = "Loading..." }) => {
  const theme = useTheme();

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          gap: 3,
          p: 3,
        }}
      >
        {/* Stylish loading animation */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Outer ring */}
          <Box
            sx={{
              position: "absolute",
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: `2px solid ${theme.palette.primary.main}20`,
              animation: "spin 3s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />

          {/* Middle ring */}
          <Box
            sx={{
              position: "absolute",
              width: 60,
              height: 60,
              borderRadius: "50%",
              border: `3px solid transparent`,
              borderTop: `3px solid ${theme.palette.primary.main}60`,
              borderRight: `3px solid ${theme.palette.primary.main}60`,
              animation: "spin 2s linear infinite reverse",
            }}
          />

          {/* Inner circle with pulse */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              animation: "pulse 2s ease-in-out infinite",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "@keyframes pulse": {
                "0%, 100%": {
                  transform: "scale(1)",
                  boxShadow: `0 0 0 0 ${theme.palette.primary.main}40`,
                },
                "50%": {
                  transform: "scale(1.1)",
                  boxShadow: `0 0 0 10px ${theme.palette.primary.main}00`,
                },
              },
            }}
          >
            {/* Inner dot */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "white",
                animation: "fade 1.5s ease-in-out infinite",
                "@keyframes fade": {
                  "0%, 100%": { opacity: 0.7 },
                  "50%": { opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>

        {/* Loading text with subtle animation */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 1,
              animation: "fadeInOut 2s ease-in-out infinite",
              "@keyframes fadeInOut": {
                "0%, 100%": { opacity: 0.7 },
                "50%": { opacity: 1 },
              },
            }}
          >
            {title}
          </Typography>

          {/* Animated dots */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 0.5,
              mt: 2,
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  animation: "bounce 1.4s ease-in-out infinite",
                  animationDelay: `${index * 0.2}s`,
                  "@keyframes bounce": {
                    "0%, 80%, 100%": {
                      transform: "scale(0.8)",
                      opacity: 0.5,
                    },
                    "40%": {
                      transform: "scale(1.2)",
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default LoadingSite;
